import { html, css, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import { buttonStyles } from '../styles/button-styles.js';
import '../ui/modal-dialog.js';
import type { ModalDialog } from '../ui/modal-dialog.js';

/**
 * Generic confirmation dialog with confirm/cancel buttons.
 * Wraps <modal-dialog> with size="small".
 * Use the default slot for custom content between message and buttons.
 *
 * @slot - Custom content (e.g., slice name, session info)
 *
 * @fires confirm - Fired when confirm button is clicked
 * @fires dialog-cancel - Fired when cancel button is clicked or modal closed
 */
@customElement('confirm-dialog')
export class ConfirmDialog extends BaseElement {
    static styles = [
        BaseElement.styles,
        buttonStyles,
        css`
            .message {
                color: var(--text-primary);
                margin: 0 0 1rem;
            }

            .error-message {
                color: var(--error-color, #ef4444);
                font-size: 0.875rem;
                text-align: center;
                margin: 1rem 0;
                padding: 0.75rem;
                background-color: rgba(239, 68, 68, 0.1);
                border: 1px solid var(--error-color, #ef4444);
                border-radius: 0.375rem;
            }

            .actions {
                display: flex;
                gap: 1rem;
                margin-top: 1.5rem;
            }

            .actions .btn {
                flex: 1;
            }
        `,
    ];

    @property({ type: String })
    heading = '';

    @property({ type: String })
    message = '';

    @property({ type: String, attribute: 'confirm-label' })
    confirmLabel = 'Confirm';

    @property({ type: String, attribute: 'confirm-variant' })
    confirmVariant: 'primary' | 'danger' = 'primary';

    @property({ type: String, attribute: 'cancel-label' })
    cancelLabel = 'Cancel';

    @property({ type: String })
    error = '';

    @property({ type: Boolean })
    loading = false;

    @property({ type: Boolean, reflect: true })
    open = false;

    @query('modal-dialog')
    private _modal!: ModalDialog;

    show(): void {
        this.open = true;
        this._modal?.show();
    }

    hide(): void {
        this.open = false;
        this.error = '';
        this.loading = false;
        this._modal?.hide();
    }

    private _onConfirm(): void {
        this.dispatchEvent(
            new CustomEvent('confirm', {
                bubbles: true,
                composed: true,
            })
        );
    }

    private _onCancel(): void {
        this.hide();
        this.dispatchEvent(
            new CustomEvent('dialog-cancel', {
                bubbles: true,
                composed: true,
            })
        );
    }

    private _onModalClose(): void {
        this.open = false;
        this.dispatchEvent(
            new CustomEvent('dialog-cancel', {
                bubbles: true,
                composed: true,
            })
        );
    }

    render() {
        return html`
            <modal-dialog
                .heading=${this.heading}
                .open=${this.open}
                size="small"
                @modal-close=${this._onModalClose}
            >
                <p class="message">${this.message}</p>
                <slot></slot>
                ${this.error
                    ? html`<p class="error-message">${this.error}</p>`
                    : nothing}
                <div slot="footer" class="actions">
                    <button
                        class="btn btn-${this.confirmVariant}"
                        @click=${this._onConfirm}
                        ?disabled=${this.loading}
                    >
                        ${this.loading ? 'Processing...' : this.confirmLabel}
                    </button>
                    <button
                        class="btn btn-secondary"
                        @click=${this._onCancel}
                    >
                        ${this.cancelLabel}
                    </button>
                </div>
            </modal-dialog>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'confirm-dialog': ConfirmDialog;
    }
}
